import React from 'react';
import { Eye, X, ExternalLink } from 'lucide-react';
import { usePreviewMode } from '../hooks/usePreviewMode';

/**
 * PreviewBanner — floating banner shown on public pages when preview mode is active.
 * Only visible to signed-in admins. Non-admins never see the banner even if the
 * ?preview=1 flag is present in the URL.
 */
export default function PreviewBanner({ isDraft, draftMeta, pageLabel }) {
  const { preview, previewRequested, isAdmin, exitPreview } = usePreviewMode();

  // Don't render banner unless the visitor is an admin AND preview is on
  if (!preview) return null;

  const timeAgo = draftMeta?.updated_at ? formatRelative(new Date(draftMeta.updated_at)) : null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg"
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      data-testid="preview-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between flex-wrap gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 bg-black/20 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">
            <Eye className="w-3.5 h-3.5" /> Preview Mode
          </div>
          {isDraft ? (
            <span className="font-medium truncate">
              Viewing unpublished draft
              {pageLabel && <> · <span className="opacity-90">{pageLabel}</span></>}
              {timeAgo && <span className="opacity-80 text-xs ml-1">(saved {timeAgo}{draftMeta?.updated_by ? ` by ${draftMeta.updated_by}` : ''})</span>}
            </span>
          ) : (
            <span className="opacity-90 truncate">
              No draft yet — showing published content
              {pageLabel && <> · <span className="opacity-90">{pageLabel}</span></>}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href="/admin"
            className="text-xs bg-white/10 hover:bg-white/20 rounded px-2 py-1 flex items-center gap-1 transition-colors"
            data-testid="preview-back-to-admin"
          >
            <ExternalLink className="w-3 h-3" /> Back to admin
          </a>
          <button
            onClick={exitPreview}
            className="text-xs bg-black/30 hover:bg-black/50 rounded px-2 py-1 flex items-center gap-1 transition-colors"
            data-testid="preview-exit-btn"
          >
            <X className="w-3 h-3" /> Exit preview
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRelative(date) {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
